import React, { useEffect, useRef, useCallback } from 'react'

interface UseIntersectionObserverOptions {
  root?: Element | null
  rootMargin?: string
  threshold?: number
  once?: boolean
  enabled?: boolean
  onIntersect: () => void
}

export function useIntersect(
  ref: React.RefObject<Element | null>,
  {
    root = null,
    rootMargin = '0px',
    threshold = 0,
    once = false,
    enabled = true,
    onIntersect,
  }: UseIntersectionObserverOptions
) {
  const hasTriggeredRef = useRef(false)

  useEffect(() => {
    const target = ref.current
    if (!target || !enabled) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && (!once || !hasTriggeredRef.current)) {
          hasTriggeredRef.current = true
          onIntersect()
        }
      },
      {
        root,
        rootMargin,
        threshold,
      }
    )

    observer.observe(target)

    return () => {
      observer.disconnect()
    }
  }, [ref, root, rootMargin, threshold, once, enabled, onIntersect])

  const reset = useCallback(() => {
    hasTriggeredRef.current = false
  }, [])

  return { reset }
}
